import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { StyleSheet, Pressable, Platform, View } from 'react-native';
import { useRouter } from 'expo-router';
import { useTheme } from '@/context/ThemeContext';
import SidebarListItemLayout from '@/components/Sidebar/SidebarListItemLayout';
import SidebarListItemChat from '@/components/Sidebar/SidebarListItemChat';
import CollectionDropdown from '@/components/Dropdowns/CollectionDropdown';
import Text from '@/components/Typography/Text';
import Tooltip from '@/components/Tooltip';
import Icon from '@/components/Icon';
import { useCollectionStore, useModalStore, useToastStore } from '@/store';
import { useModalHelper } from '@/hooks/useModalHelper';
import useTranslation from '@/hooks/useTranslation';
import { icons as collectionIcons } from '@/components/CollectionIconsGrid';
import * as DropdownMenu from '@/components/DropdownMenu/DropdownMenu';
import { getDescendantIds, MAX_FOLDER_DEPTH } from '@/constants/utils/folder_tree';

const createStyles = (colors, isActive, isHovered) => StyleSheet.create({
  container: {
    width: '100%',
  },
  titleContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    minWidth: 0,
  },
  title: {
    color: isActive ? colors.accent_text_color : colors.text_color_primary,
    textDecorationLine: isHovered ? 'underline' : 'none',
    cursor: 'pointer',
    flex: 1,
    minWidth: 0,
  },
  titleButton: {
    padding: 4,
    margin: -4,
    borderRadius: 4,
    flex: 1,
    minWidth: 0,
  },
});

export default function SidebarListItemCollection({
  title,
  isActive,
  href,
  id,
  chats,
  childCollections = [],
  indentation = 0,
  ...props
}) {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const router = useRouter();

  const { useInputModal, useConfirmationModal } = useModalHelper();
  const showInputModal = useInputModal();
  const showConfirmationModal = useConfirmationModal();

  const renameCollection = useCollectionStore(state => state.renameCollection);
  const updateCollection = useCollectionStore(state => state.updateCollection);
  const deleteCollection = useCollectionStore(state => state.deleteCollection);
  const collectionIcon = useCollectionStore(state => state.collections[id]?.icon) || 'folder';
  const { openModal } = useModalStore(state => state.actions);
  const { addToast } = useToastStore(state => state.actions);

  const styles = useMemo(() => createStyles(colors, isActive, isHovered), [colors, isActive, isHovered]);

  const collectionsMap = useCollectionStore(state => state.collections);

  const hasChildCollections = Array.isArray(childCollections) && childCollections.length > 0;

  const nodeContainsActive = useCallback((node) => {
    if (!node) return false;
    if (node.isActive) return true;
    if (node.chats?.some(chat => chat.isActive)) return true;
    return (node.children || []).some(nodeContainsActive);
  }, []);

  // Check if any chat is active or collection is active and auto-expand
  useEffect(() => {
    const hasActiveChat = chats?.some(chat => chat.isActive);
    const hasActiveDescendant = childCollections.some(nodeContainsActive);
    if (hasActiveChat || isActive || hasActiveDescendant) {
      setIsExpanded(true);
    }
  }, [chats, isActive, childCollections, nodeContainsActive]);

  const handleChevronClick = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();

    const hasChats = Array.isArray(chats) && chats.length > 0;

    // If there are no chats or nested collections, go directly to the collection page
    if (!hasChats && !hasChildCollections && href) {
      router.push(href);
      return;
    }

    // Otherwise, just toggle expand/collapse
    const willExpand = !isExpanded;
    setIsExpanded(willExpand);
  }, [chats, href, isExpanded, router, hasChildCollections]);

  const handleTitleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (href) {
      router.push(href);
    }
  };

  const handleRename = useCallback(() => {
    showInputModal({ 
      title: t('sidebar.renameCollection'),
      subtitle: t('sidebar.renameCollectionSubtitle'),
      placeholder: t('sidebar.collectionNamePlaceholder'),
      primaryButtonTitle: t('common.save'),
      initialValue: title,
      onSave: async (newName) => {
        try {
          await renameCollection(id, newName);
        } catch (error) {
          console.error('Failed to rename collection:', error);
          addToast(t('sidebar.failedToRenameCollection'), 'error');
        }
      },
    });
  }, [title, id, renameCollection, showInputModal, addToast, t]);

  const handleDelete = useCallback(() => {
    showConfirmationModal({
      title: t('sidebar.deleteCollectionTitle').replace('%NAME%', title || 'Collection'),
      message: t('sidebar.deleteCollectionMessage'),
      confirmText: t('common.delete'),
      isDangerous: true,
      onConfirm: async () => {
        try {
          await deleteCollection(id);
          router.push('/files');
        } catch (error) {
          console.error('Failed to delete collection:', error);
          addToast(t('sidebar.failedToDeleteCollection'), 'error');
        }
      },
    });
  }, [title, id, deleteCollection, router, showConfirmationModal, addToast, t]);

  const handleChangeIcon = useCallback(() => {
    openModal('CollectionsIconGridModal', {
      selectedIcon: collectionIcon,
      onIconSelect: async (iconKey) => {
        try {
          await updateCollection(id, { icon: iconKey });
          addToast(t('sidebar.collectionIconUpdated'), 'success');
        } catch (error) {
          console.error('Failed to update collection icon:', error);
          addToast(t('sidebar.failedToUpdateIcon'), 'error');
        }
      },
    });
  }, [openModal, collectionIcon, id, updateCollection, addToast]);

  const handleNewSubcollection = useCallback(() => {
    if (indentation >= MAX_FOLDER_DEPTH - 1) {
      addToast(t('pages.collections.nestedTooDeep'), 'error');
      return;
    }
    openModal('CreateCollectionModal', {
      parentId: id,
      parentTitle: title,
    });
  }, [indentation, addToast, t, openModal, id, title]);

  const handleMove = useCallback(() => {
    const collections = Object.values(collectionsMap || {});
    const excludeIds = [id, ...getDescendantIds(collections, id)];
    openModal('MoveCollectionModal', {
      collectionId: id,
      excludeIds,
    });
  }, [collectionsMap, id, openModal]);

  const dropdownContent = useMemo(() => (
    <CollectionDropdown
      noRoot
      handleChangeIcon={handleChangeIcon}
      handleRename={handleRename}
      handleDelete={handleDelete}
      handleNewSubcollection={handleNewSubcollection}
      handleMove={handleMove}
      title={title}
    />
  ), [handleChangeIcon, handleRename, handleDelete, handleNewSubcollection, handleMove, title]);

  const titleContent = (
    <Pressable
      onPress={handleTitleClick}
      onHoverIn={() => setIsHovered(true)}
      onHoverOut={() => setIsHovered(false)}
      style={styles.titleButton}
      data-title-button="true"
    >
      <Text numberOfLines={1} style={styles.title}>{title}</Text>
    </Pressable>
  );

  // Get the icon name from the collection icon key
  const iconData = collectionIcons[collectionIcon] || collectionIcons.folder;
  const iconName = iconData.icon;
  // Use folder-open when expanded if the icon is folder, otherwise use the collection icon
  const displayIcon = (collectionIcon === 'folder' && isExpanded) ? 'folder-open' : iconName;

  const collectionActions = Platform.OS === 'macos' ? (
    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
      <Tooltip label={isExpanded ? t('sidebar.collapse') : t('sidebar.expand')}>
        <Pressable onPress={handleChevronClick}>
          <Icon
            name={isExpanded ? 'chevron-down' : 'chevron-right'}
            size={14}
            color={isActive ? colors.accent_text_color : colors.text_color_secondary}
          />
        </Pressable>
      </Tooltip>
      <DropdownMenu.Root>
        <DropdownMenu.Trigger>
          <Pressable
            onPress={(e) => {
              e?.preventDefault?.();
              e?.stopPropagation?.();
            }}
            style={{
              width: 24,
              height: 24,
              borderRadius: 4,
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icon
              name="ellipsis"
              size={14}
              color={isActive ? colors.accent_text_color : colors.text_color_secondary}
            />
          </Pressable>
        </DropdownMenu.Trigger>
        <CollectionDropdown
          noRoot
          handleChangeIcon={handleChangeIcon}
          handleRename={handleRename}
          handleDelete={handleDelete}
          handleNewSubcollection={handleNewSubcollection}
          handleMove={handleMove}
          title={title}
        />
      </DropdownMenu.Root>
    </View>
  ) : (
    <Tooltip label={isExpanded ? t('sidebar.collapse') : t('sidebar.expand')}>
      <Pressable onPress={handleChevronClick}>
        <Icon
          name={isExpanded ? 'chevron-down' : 'chevron-right'}
          size={14}
          color={isActive ? colors.accent_text_color : colors.text_color_secondary}
        />
      </Pressable>
    </Tooltip>
  );

  return (
    <View style={styles.container}>
      <SidebarListItemLayout
        title={titleContent}
        icon={displayIcon}
        isActive={isActive}
        onPress={href ? () => router.push(href) : undefined}
        rightIcon={collectionActions}
        dropdownContent={Platform.OS === 'macos' ? undefined : dropdownContent}
        noEllipsis={Platform.OS === 'macos'}
        {...props}
        indentation={indentation}
      />
      {isExpanded && hasChildCollections && childCollections.map((child) => (
        <SidebarListItemCollection
          key={`sidebar-list-item-collection-${child.id}`}
          id={child.id}
          title={child.title}
          href={child.href}
          isArchived={child.isArchived}
          isActive={child.isActive}
          chatCount={child.chatCount}
          chats={child.chats}
          childCollections={child.children || []}
          indentation={indentation + 1}
        />
      ))}
      {isExpanded && Array.isArray(chats) && chats?.map(chat => (
        <SidebarListItemChat
          key={chat.id}
          {...chat}
          indentation={indentation + 1}
        />
      ))}
    </View>
  );
} 